import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { OepaUserAccount } from '@ohioepa/oepa-common-lib';
import { OepaIopAuthLibService } from '@ohioepa/oepa-iop-auth-lib';
import { OepaUserAccountService } from '@ohioepa/oepa-ui-lib';
import { ToastrService } from 'ngx-toastr';
import { intLiveReloadAccount } from './int-live-reload-account';
import { extLiveReloadAccount } from './ext-live-reload-account';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit {
  title = 'Ohio EPA Asbestos';
  currentUserID = '';
  currentUserName = '';
  userAccount!: OepaUserAccount;
  uniqueID: string = '';
  private personID: string = '';
  private sessionID: string = '';
  private logIn: string = '';

  private loadBootstrapTheme() {
    const link = document.createElement('link');
    link.setAttribute('rel', 'stylesheet');
    link.type = 'text/css';
    link.href = 'https://cdn.jsdelivr.net/npm/bootswatch@5.3.2/dist/spacelab/bootstrap.min.css';
    document.head.appendChild(link);
  }

   constructor(private route: ActivatedRoute, private router: Router,
    private authService: OepaIopAuthLibService,
    private toastr: ToastrService,
    private userAccountService: OepaUserAccountService) {
  }

  ngOnInit() {
    this.loadBootstrapTheme();
    this.getEbizVersion();
    this.route.queryParams.subscribe((params: Params) => {
      this.getQueryParameters(params);
      let loginInitialized = sessionStorage.getItem('LOGIN_INITIALIZED');
      if (!loginInitialized) {
        this.loadApp(params);
      }
      if (params['code']) {
        this.login();
      }
    });
  }

  private initalLogin() {
    this.authService.configureOAuth();
    setTimeout(() => {
      this.authService.login();
      sessionStorage.setItem('LOGIN_INITIALIZED', 'true');
    }, 1000)
  }

  async login() {
    this.authService.configureOAuth();
    setTimeout(async () => {
      let authenticated: Promise<boolean> = this.authService.tryLogin();
      if (await authenticated) {
        this.retrieveUserInfo();
      }
    }, 500);
  }

  private retrieveIOPUserAccount(userID: string) {
    this.userAccountService.setAuthToken(this.authService.getAccessToken());
    this.userAccountService.retrieveAccountForUser(userID, 'ASBESTOS').subscribe({
      next: (account: OepaUserAccount) => {
        if (account) {
          this.initializeUserAccount(account);
        } else {
          console.log('No account found');
        }
      }, error: err => {
        this.toastr.error('Initialization error: ' + err, 'ERROR');
      }
    });
  }

    private retrieveInternalIOPUserAccount(ohid: string) {
    this.userAccountService.setAuthToken(this.authService.getAccessToken());
    this.userAccountService.retrieveAccountByOHID(ohid, 'ASBESTOS').subscribe({
      next: (account: OepaUserAccount) => {
        if (account) {
          this.initializeUserAccount(account);
        } else {
          console.log('No account found');
        }
      }, error: err => {
        this.toastr.error('Initialization error: ' + err, 'ERROR');
      }
    });
  }
  
    private retrieveLiveReloadAccount() {
      sessionStorage.setItem('LOGIN_INITIALIZED', 'true');
      let liveReloadAccount = this.getEnv() === 'ext' ? extLiveReloadAccount : intLiveReloadAccount;
        let overrrideUserID = this.getUserID();
        if (overrrideUserID) {
          liveReloadAccount.loginName = overrrideUserID;
          liveReloadAccount.accountID = overrrideUserID;
        }
      this.userAccountService.setLiveReloadUser(liveReloadAccount).subscribe(() => {
          this.userAccountService.retrieveAccountForCurrentUser('ASBESTOS').subscribe({
            next: (account: OepaUserAccount) => {
              if (account) {
                this.initializeUserAccount(account);
                this.userAccountService.userAccount$.next({});
              } else {
                console.log('No account found');
              }
            }, error: err => {
              this.toastr.error('Initialization error: ' + err, 'ERROR');
            }
          });
      });
    }

  private retrieveEBizUserAccount(userID: string) {
    this.userAccountService.setAuthToken(this.authService.getAccessToken());
    this.userAccountService.retrieveAccountForUser(userID, 'ASBESTOS').subscribe({
      next: (account: OepaUserAccount) => {
        if (account) {
          this.initializeUserAccount(account);
        } else {
          console.log('No account found');
        }
      }, error: err => {
        this.toastr.error('Initialization error: ' + err, 'ERROR');
      }
    });
  }

  private retrieveWebAppsUserAccount() {
    this.userAccountService.setAuthToken(this.authService.getAccessToken());
    this.userAccountService.retrieveAccountForInternalSession(this.personID, this.sessionID, 'ASBESTOS').subscribe({
      next: (account: OepaUserAccount) => {
        if (account) {
          this.initializeUserAccount(account);
        } else {
          console.log('No account found');
        }
      }, error: err => {
        this.toastr.error('Initialization error: ' + err, 'ERROR');
      }
    });
  }

  private initializeUserAccount(account: OepaUserAccount) {
    if (account) {
      this.userAccount = account;
      this.currentUserName = account.firstName || '';
      this.currentUserID = account.loginName || '';
      this.uniqueID = account.portalSessionID || '';
      this.userAccountService.setCurrentUserAccount(account);
      this.routeToApplication();
    } else {
      console.log('No account found');
    }
  }

  retrieveUserInfo() {
    if (this.getEnv() === 'ext') {
      this.authService.userProfile$.subscribe((profile) => {
        this.retrieveIOPUserAccount(this.getUserID());
      });
    } else {
      this.authService.userProfile$.subscribe((profile) => {
        let ohid = profile.eidmuserid;
        this.retrieveInternalIOPUserAccount(ohid) ;
      });
    }
      
  }

  private loadApp(params: Params) {
    if (this.isLocalDevelopment()) {
      this.retrieveLiveReloadAccount();
    } else {
      if (this.getLoginParam()) {
        if (this.logIn) {
          this.retrieveEBizUserAccount(this.logIn);
        } else {
          this.retrieveWebAppsUserAccount();
        }
          
      } else {
        let loginInitialized = sessionStorage.getItem('LOGIN_INITIALIZED');
        if (!loginInitialized) {
          this.initalLogin();
        }
      }
    }
  }

  private isLocalDevelopment(): boolean {
    return (window.location.href.indexOf('localhost') > -1 && window.location.href.indexOf('7001') < 0);
  }

  private getQueryParameters(params: Params) {
    if (params['env']) {
      this.setEnv(params['env']);
    }
    if (params['uid']) {
      this.setUserID(params['uid']);
    }
    this.logIn = params['login'];
    this.personID = params['personID'];
    this.sessionID = params['sessionID'];
  }

  ngOnDestroy(): void {
    sessionStorage.clear();
  }

  private routeToApplication() {
    if (this.getEnv() === 'ext') {
      this.router.navigate(['ext'], { queryParams: { uid: this.getUserID() } });
    } else {
      this.router.navigate(['int']);
    }
      
  }

  private setEnv(env: string) {
    sessionStorage.setItem('env', env);
  }

  private getEnv(): string {
    return sessionStorage.getItem('env') || '';
  }

  private setUserID(userID: string) {
    sessionStorage.setItem('USER_ID', userID);
  }

  private getUserID(): string {
    return sessionStorage.getItem('USER_ID') || '';
  }

  private setLoginParam(loginParam: string) {
    sessionStorage.setItem('LOGIN_PARAM', loginParam);
  }

  private getLoginParam(): boolean {
    return !!(this.logIn || this.sessionID);
  }

  private getEbizVersion() {
    this.userAccountService.getEBizVersion('ASBESTOS').subscribe(ebizVersion => {
      if (ebizVersion) {
        sessionStorage.setItem('EBIZ_VERSION', ebizVersion);
      }
    })
  }
}

